import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { createTableRow, deleteTableRows, fetchTableRows, updateTableRow } from '../../../../redux/slices/tableRowSlice';

interface EditTableRowsProps {
  tableIndex: number | null;
  handleClose: () => void;
}

interface TableColumn {
  name?: string;
  column_name?: string;
  label?: string;
  type?: string;
}

interface TableDefinition {
  table_id: number;
  table_name: string;
  zone_name: string;
  table_columns: TableColumn[];
}

interface TableEditorRow {
  table_id: number;
  table_row_id: number;
  values: Record<string, any>;
}

interface DraftRow {
  clientId: string;
  tableRowId: number | null;
  values: Record<string, any>;
}

const normalizeCellValue = (value: any) => (value === undefined || value === '' ? null : value);

const extractColumnNames = (tableColumns: TableColumn[] = []) =>
  tableColumns
    .map((column) => String(column?.name ?? column?.column_name ?? column?.label ?? '').trim())
    .filter((columnName, index, arr) => Boolean(columnName) && arr.indexOf(columnName) === index);

const extractColumnsFromRows = (tableRows: TableEditorRow[] = []) =>
  Array.from(new Set(tableRows.flatMap((row) => (row?.values && typeof row.values === 'object' ? Object.keys(row.values) : []))));

const buildRowValues = (values: Record<string, any> = {}, columns: string[]): Record<string, any> =>
  columns.reduce(
    (acc, columnName) => {
      acc[columnName] = values[columnName] ?? null;
      return acc;
    },
    {} as Record<string, any>
  );

const cloneDraftRows = (rows: DraftRow[]): DraftRow[] =>
  rows.map((row) => ({
    ...row,
    values: { ...row.values },
  }));

const areRowValuesEqual = (leftValues: Record<string, any>, rightValues: Record<string, any>, columns: string[]) =>
  columns.every((columnName) => (leftValues[columnName] ?? null) === (rightValues[columnName] ?? null));

const EditTableRows: React.FC<EditTableRowsProps> = ({ tableIndex, handleClose }) => {
  const dispatch = useAppDispatch();
  const tables = useAppSelector((state) => state.tables.items);
  const table = useMemo<TableDefinition | null>(
    () =>
      tableIndex !== null
        ? // TODO #757 step 3 PR D: align TableItem (slice) with TableDefinition
          ((tables[tableIndex] as unknown as TableDefinition) ?? null)
        : null,
    [tableIndex, tables]
  );

  const tableKey = table ? `${table.zone_name}:${table.table_name}` : null;
  const tableColumns = useMemo(() => extractColumnNames(table?.table_columns ?? []), [table]);

  const clientIdCounterRef = useRef(0);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [draftRows, setDraftRows] = useState<DraftRow[]>([]);
  const [baselineRows, setBaselineRows] = useState<DraftRow[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [deletedRowIds, setDeletedRowIds] = useState<number[]>([]);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const createClientId = useCallback(() => {
    clientIdCounterRef.current += 1;
    return `draft-row-${clientIdCounterRef.current}`;
  }, []);

  const hydrateRows = useCallback(
    (tableRows: TableEditorRow[], nextColumns: string[]) => {
      const nextBaselineRows = tableRows.map((row) => ({
        clientId: createClientId(),
        tableRowId: row.table_row_id,
        values: buildRowValues(row.values, nextColumns),
      }));

      setVisibleColumns(nextColumns);
      setBaselineRows(nextBaselineRows);
      setDraftRows(cloneDraftRows(nextBaselineRows));
      setDeletedRowIds([]);
      setSelectedClientIds([]);
      setShowCloseConfirm(false);
    },
    [createClientId]
  );

  useEffect(() => {
    if (!table) {
      setVisibleColumns([]);
      setBaselineRows([]);
      setDraftRows([]);
      setDeletedRowIds([]);
      setSelectedClientIds([]);
      return;
    }

    let isActive = true;

    const loadRows = async () => {
      try {
        const fetchedRows = await dispatch(
          fetchTableRows({
            zone_name: table.zone_name,
            table_name: table.table_name,
          })
        ).unwrap();

        const nextColumns = tableColumns.length > 0 ? tableColumns : extractColumnsFromRows(fetchedRows);

        if (isActive) {
          hydrateRows(fetchedRows, nextColumns);
        }
      } catch (error) {
        if (!isActive) {
          return;
        }

        setVisibleColumns([]);
        setBaselineRows([]);
        setDraftRows([]);
        setDeletedRowIds([]);
        setSelectedClientIds([]);
        toast.error('Failed to load table rows.');
      }
    };

    loadRows();

    return () => {
      isActive = false;
    };
  }, [dispatch, hydrateRows, table, tableColumns, tableKey]);

  const hasUnsavedChanges = useMemo(() => {
    if (deletedRowIds.length > 0) {
      return true;
    }

    if (draftRows.length !== baselineRows.length) {
      return true;
    }

    return draftRows.some((draftRow, rowIndex) => {
      const baselineRow = baselineRows[rowIndex];

      if (!baselineRow || draftRow.tableRowId !== baselineRow.tableRowId) {
        return true;
      }

      return !areRowValuesEqual(draftRow.values, baselineRow.values, visibleColumns);
    });
  }, [baselineRows, deletedRowIds.length, draftRows, visibleColumns]);

  const updateDraftCell = useCallback((clientId: string, columnName: string, value: any) => {
    const nextValue = normalizeCellValue(value);

    setDraftRows((prevDraftRows) =>
      prevDraftRows.map((draftRow) => {
        if (draftRow.clientId !== clientId) {
          return draftRow;
        }

        if ((draftRow.values[columnName] ?? null) === nextValue) {
          return draftRow;
        }

        return {
          ...draftRow,
          values: {
            ...draftRow.values,
            [columnName]: nextValue,
          },
        };
      })
    );
  }, []);

  const handleRowSelectionChange = useCallback((clientId: string, isSelected: boolean) => {
    setSelectedClientIds((prevSelectedClientIds) => {
      if (isSelected) {
        return prevSelectedClientIds.includes(clientId) ? prevSelectedClientIds : [...prevSelectedClientIds, clientId];
      }

      return prevSelectedClientIds.filter((selectedClientId) => selectedClientId !== clientId);
    });
  }, []);

  const handleAddRow = useCallback(() => {
    setDraftRows((prevDraftRows) => [
      ...prevDraftRows,
      {
        clientId: createClientId(),
        tableRowId: null,
        values: buildRowValues({}, visibleColumns),
      },
    ]);
  }, [createClientId, visibleColumns]);

  const handleDeleteSelected = useCallback(() => {
    const selectedClientIdSet = new Set(selectedClientIds);
    const rowsToDelete = draftRows
      .filter((row) => selectedClientIdSet.has(row.clientId))
      .map((row) => row.tableRowId)
      .filter((tableRowId): tableRowId is number => tableRowId !== null);

    setDraftRows((prevDraftRows) => prevDraftRows.filter((row) => !selectedClientIdSet.has(row.clientId)));
    setDeletedRowIds((prevDeletedRowIds) => Array.from(new Set([...prevDeletedRowIds, ...rowsToDelete])));
    setSelectedClientIds([]);
  }, [draftRows, selectedClientIds]);

  const handleSaveRows = async () => {
    if (!table) {
      return;
    }

    const draftRowsSnapshot = cloneDraftRows(draftRows);
    const deletedRowIdsSnapshot = [...deletedRowIds];
    const baselineRowsById = new Map<number, DraftRow>(
      baselineRows.filter((row): row is DraftRow & { tableRowId: number } => row.tableRowId !== null).map((row) => [row.tableRowId, row])
    );

    try {
      if (deletedRowIdsSnapshot.length > 0) {
        await dispatch(
          deleteTableRows({
            zone_name: table.zone_name,
            table_name: table.table_name,
            table_row_ids: deletedRowIdsSnapshot,
          })
        ).unwrap();
      }

      const updateOperations: Promise<any>[] = [];
      const rowsToCreate: Record<string, any>[] = [];

      draftRowsSnapshot.forEach((draftRow) => {
        const rowValues = visibleColumns.reduce(
          (acc, columnName) => {
            acc[columnName] = draftRow.values[columnName] ?? '';
            return acc;
          },
          {} as Record<string, any>
        );

        if (draftRow.tableRowId !== null) {
          const baselineRow = baselineRowsById.get(draftRow.tableRowId);

          if (baselineRow && areRowValuesEqual(draftRow.values, baselineRow.values, visibleColumns)) {
            return;
          }

          updateOperations.push(
            dispatch(
              updateTableRow({
                table_id: table.table_id,
                table_row_id: draftRow.tableRowId,
                zone_name: table.zone_name,
                table_name: table.table_name,
                values: rowValues,
              })
            ).unwrap()
          );
          return;
        }

        rowsToCreate.push(rowValues);
      });

      await Promise.all(updateOperations);

      for (const rowValues of rowsToCreate) {
        await dispatch(
          createTableRow({
            zone_name: table.zone_name,
            table_name: table.table_name,
            values: rowValues,
          })
        ).unwrap();
      }

      const refreshedRows = await dispatch(
        fetchTableRows({
          zone_name: table.zone_name,
          table_name: table.table_name,
        })
      ).unwrap();

      const nextColumns = tableColumns.length > 0 ? tableColumns : extractColumnsFromRows(refreshedRows);

      hydrateRows(refreshedRows, nextColumns);
      toast.success('Rows saved successfully.');
    } catch (error) {
      toast.error('Failed to save table rows.');
    }
  };

  const handleRequestClose = () => {
    if (!hasUnsavedChanges) {
      handleClose();
      return;
    }

    setShowCloseConfirm(true);
  };

  if (!table) {
    return null;
  }

  return (
    <>
      <Modal show={true} onHide={handleRequestClose} fullscreen={true}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Rows for Table: {table.table_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 flex-shrink-0">
            <div className="d-flex flex-wrap gap-2 text-muted small">
              <span className="px-3 py-2 rounded-pill border bg-white">
                Zone <strong className="ms-1 text-dark">{table.zone_name}</strong>
              </span>
              <span className="px-3 py-2 rounded-pill border bg-white">
                Rows <strong className="ms-1 text-dark">{draftRows.length}</strong>
              </span>
              <span className="px-3 py-2 rounded-pill border bg-white">
                Columns <strong className="ms-1 text-dark">{visibleColumns.length}</strong>
              </span>
            </div>
            <div className="text-muted small">
              Changes stay local until you use <strong>Save All</strong>.
            </div>
          </div>

          <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
            <table className="table table-sm table-bordered align-middle mb-0" style={{ width: '100%' }}>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Select</th>
                  {visibleColumns.map((columnName) => (
                    <th key={columnName} style={{ minWidth: '200px' }}>
                      {columnName}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {draftRows.map((draftRow, rowIndex) => (
                  <tr key={draftRow.clientId}>
                    <td style={{ width: '1%', whiteSpace: 'nowrap' }}>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="checkbox"
                          aria-label={`select-row-${rowIndex}`}
                          checked={selectedClientIds.includes(draftRow.clientId)}
                          onChange={(event) => {
                            handleRowSelectionChange(draftRow.clientId, event.target.checked);
                          }}
                        />
                        <span>{rowIndex + 1}</span>
                      </div>
                    </td>
                    {visibleColumns.map((columnName) => (
                      <td key={`${draftRow.clientId}-${columnName}`}>
                        <Form.Control
                          type="text"
                          size="sm"
                          aria-label={`${columnName}-${rowIndex}`}
                          value={draftRow.values[columnName] ?? ''}
                          onChange={(event) => {
                            updateDraftCell(draftRow.clientId, columnName, event.target.value);
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
                {draftRows.length === 0 ? (
                  <tr>
                    <td colSpan={Math.max(visibleColumns.length + 1, 2)} className="text-center text-muted py-4">
                      No rows available.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div style={{ width: '100%', marginBottom: '10px', textAlign: 'center' }}>
            <Button variant="primary" onClick={handleAddRow} className="me-2">
              Add Row
            </Button>
            <Button variant="danger" onClick={handleDeleteSelected} className="me-2" disabled={selectedClientIds.length === 0}>
              Delete Selected
            </Button>
          </div>
          <Button variant="secondary" onClick={handleRequestClose}>
            Close
          </Button>
          <Button variant="success" onClick={handleSaveRows}>
            Save All
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCloseConfirm} onHide={() => setShowCloseConfirm(false)} centered contentClassName="border border-300 shadow-sm">
        <Modal.Header closeButton>
          <Modal.Title>Confirm close</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>You have unsaved changes. Are you sure you want to close this row editor?</p>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCloseConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setShowCloseConfirm(false);
              handleClose();
            }}
          >
            Confirm Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditTableRows;
